import React, { useId, useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

/** Circular meter used for stats / proficiency infographics. */
export default function StatRing({
  value,
  suffix = '',
  label,
  max = 100,
  size = 112,
  stroke = 7,
  className = ''
}) {
  const ref = useRef(null);
  const gradientId = useId().replace(/:/g, '');
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const prefersReducedMotion = useReducedMotion();

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(value / max, 1);
  const offset = circumference * (1 - (inView || prefersReducedMotion ? progress : 0));

  return (
    <div ref={ref} className={`flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-slate-200 dark:text-white/[0.06]"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: prefersReducedMotion ? 0 : 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3f8cff" />
              <stop offset="55%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#34d399" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            {value}
            <span className="text-emerald-500">{suffix}</span>
          </span>
        </div>
      </div>
      {label && (
        <p className="mt-3 max-w-[8rem] text-center font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
          {label}
        </p>
      )}
    </div>
  );
}
